import { createAsyncThunk, createSlice, isPending } from "@reduxjs/toolkit";
import { addToMyPlansAsync, updatePlanAsync } from "components/features/MyPlanDetails/MyPlanDetails.slice";
import { uniq, uniqBy } from "lodash";
import { PAGE_SIZE, abovePropertyViewTypePlan, abovePropertyViewTypeTemplate } from "utils/CommonConstants";

import { apiRequest } from "services/ServiceUtil";
import { LIBRARY_SERVICE_METHOD, USER_PLAN_SERVICE_METHOD, USER_SERVICES_METHOD } from "services/commonService";
import { deleteMyPlansAsync } from "../MyPlans/myplans.slice";
import { fetchBookmarkedInncodeAsync, updateBookmarkedInncodesAsync } from "../dashboard/Dashboard.slice";
import { AbovePropertyState } from "./AboveProperty";

const filtersInitialState = {
  dialogFilters: {
    bookmarkProperty: {
      searchText: "",
      brandCodes: [],
      engagements: [],
      filteredInnCodes: []
    }
  },

  myPlans: {
    templatePlans: {
      functionalAreas: []
    }
  },
  userView: {
    plans: {
      status: [],
      source: [],
      searchText: "",
      functionalAreas: []
    }
  },
  corpUserProgress: {
    plan: {
      engagements: [],
      inncode: [],
      status: [],
      searchText: ""
    },
    property: {
      functionalAreas: [],
      owners: [],
      searchText: ""
    },
    users: {
      functionalAreas: [],
      searchText: ""
    }
  }
};

export const initialState: AbovePropertyState = {
  brands: [],
  allProperties: [],
  multiTargetDetails: [],
  multiTargetTotalRecords: 0,
  totalRecords: 0,
  propertyList: [],
  myPlanDetails: {},
  aboveProperties: [],
  abovePropertiesDialog: [],
  roleCategoryList: [],
  initiativeFilterValues: [],
  bookmarkedIdList: [],
  bookmarkedTemplate: [],
  assignedLibraryPlan: [],
  bookmarkedProperties: [],
  abovePropertyLoader: false,
  bookmarkLoader: false,
  planProgress: {
    planDetail: {},
    createNewPlan: {},
    plansList: [],
    totalList: 0,
    unImplementedProperties: [],
    unImplemenetedCount: 0,
    owners: [],
    snapshotData: {}
  },
  planProperty: {
    totalRecords: 0,
    planPropertyList: []
  },
  filters: filtersInitialState,
  planProgressSummary: {},
  progressSummaryLoader: false
};

const SLICE = "ABOVE_PROPERTY";

function isRejectedAction(action: any) {
  return action.type.endsWith("rejected");
}

export const fetchAboveProperties = createAsyncThunk(`${SLICE}/fetchAboveProperties`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.postAbovePropertyPropertiesPaged, data, thunkApi)
);

export const fetchAbovePropertiesForDialog = createAsyncThunk(
  `${SLICE}/fetchAbovePropertiesDialog`,
  (data: any, thunkApi) => {
    return apiRequest(USER_PLAN_SERVICE_METHOD.postAboveProperty, data, thunkApi, ["properties"]);
  }
);

export const fetchInitiativeFilterAsync = createAsyncThunk(`${SLICE}/initiativeFilter`, (data: any, thunkApi) => {
  const queryParam = {
    innCodes: data
  };
  return apiRequest(LIBRARY_SERVICE_METHOD.getPlans, {}, thunkApi, ["initiative-filter"], queryParam);
});

export const fetchAssignedLibraryPlansAsync = createAsyncThunk(`${SLICE}/assignedPlan`, (data: any, thunkApi) => {
  return apiRequest(USER_PLAN_SERVICE_METHOD.postAboveProperty, data, thunkApi, ["assigned-plans"]);
});

export const fetchBookmarkPlan = createAsyncThunk(`${SLICE}fetchBookmarkPlan`, (data: any, thunkApi) => {
  const payload = {
    viewType: data.viewType
  };
  return apiRequest(USER_PLAN_SERVICE_METHOD.getAboveProperty, {}, thunkApi, ["bookmark"], payload);
});

export const updateBookmarkedPlan = createAsyncThunk(`${SLICE}/updateBookmarkPlan`, (data: any, thunkApi) => {
  const queryPayload = {
    viewType: data.viewType
  };
  const payload = {
    ...data.payload
  };
  return apiRequest(USER_PLAN_SERVICE_METHOD.putAboveProperty, payload, thunkApi, ["bookmark"], queryPayload);
});

export const fetchAssigneeDetailsByHtlIdsAsync = createAsyncThunk(
  `${SLICE}/assigneeDetailsByHtlIds`,
  (data: any, thunkApi) => {
    const queryParam = {
      size: data.size,
      hltIds: data.value
    };
    return apiRequest(USER_SERVICES_METHOD.getAssociates, {}, thunkApi, [], queryParam);
  }
);

export const fetchAbovePropertyMyPlansAsync = createAsyncThunk(
  `${SLICE}/aboveProperty/myplans`,
  (data: any, thunkApi) => {
    const params = {
      page: data.page,
      size: PAGE_SIZE
    };

    const payload = {
      ...data.payload
    };
    return apiRequest(USER_PLAN_SERVICE_METHOD.postAboveProperty, payload, thunkApi, ["plans"], params);
  }
);

export const fetchPropertyInfoAsync = createAsyncThunk(`${SLICE}/aboveProperty/propertyInfo`, (data: any, thunkApi) =>
  apiRequest(LIBRARY_SERVICE_METHOD.getPropertyInfo, {}, thunkApi, [], data)
);

export const fetchMultiTargetDetails = createAsyncThunk(`${SLICE}/fetchMultiTargetDetails`, (data: any, thunkApi) =>
  apiRequest(LIBRARY_SERVICE_METHOD.postPlans, data, thunkApi, ["multi-target"])
);

export const fetchUnImplementedProperties = createAsyncThunk(
  `${SLICE}/unimplemented-properties`,
  (data: any, thunkApi) => {
    return apiRequest(USER_PLAN_SERVICE_METHOD.getAboveProperty, {}, thunkApi, ["unimplemented-properties"], data);
  }
);

export const fetchSnapshotData = createAsyncThunk(`${SLICE}/snapshot`, (data: any, thunkApi) => {
  return apiRequest(USER_PLAN_SERVICE_METHOD.postAboveProperty, data, thunkApi, ["plans"]);
});

export const fetchPlansSummary = createAsyncThunk(`${SLICE}/libraryPlans`, (data: any, thunkApi) => {
  const queryPayload = {
    externalHltId: data?.externalHltId || ""
  };
  return apiRequest(LIBRARY_SERVICE_METHOD.postPlans, data, thunkApi, [], queryPayload);
});

export const AbovePropertySlice = createSlice({
  name: SLICE,
  initialState,
  reducers: {
    reset: () => initialState,
    clearPlanProgress: (state) => {
      state.planProgress = {
        planDetail: {},
        plansList: [],
        totalList: 0
      };
      state.planProgressSummary = {};
    },
    setAbovePropertyFilters: (state, action) => {
      let parentView = { ...state.filters[action?.payload?.parentView] };
      let selectedFilterCatagory = parentView[action?.payload?.filterType];
      selectedFilterCatagory[action?.payload?.filterName] = action.payload?.value;
      state.filters[action?.payload?.parentView][action?.payload?.filterType] = selectedFilterCatagory;
    },

    setSearchText: (state, action) => {
      let parentView = { ...state.filters[action?.payload?.parentView] };
      let selectedFilterCatagory = parentView[action?.payload?.searchType];
      selectedFilterCatagory[action?.payload?.filterName] = action?.payload?.inputText || "";
      state.filters[action?.payload?.parentView][action?.payload?.filterType] = selectedFilterCatagory;
    },

    clearFilter: (state, action) => {
      let parentView = { ...state.filters[action?.payload?.parentView] };
      let selectedFilterCatagory = parentView[action?.payload?.filterType];
      Object.keys(selectedFilterCatagory).forEach((key) => {
        if (Array.isArray(selectedFilterCatagory[key])) selectedFilterCatagory[key] = [];
        else if (typeof selectedFilterCatagory[key] === "boolean" && !action?.payload?.isClearAll)
          selectedFilterCatagory[key] = false;
      });
      state.filters[action?.payload?.parentView][action?.payload?.filterType] = selectedFilterCatagory;
    },

    clearChildFilters: (state) => {
      state.filters = filtersInitialState;
    },

    resetAllFilters: (state) => {
      state.filters = filtersInitialState;
    },

    setBrands: (state, action) => {
      state.brands = action.payload;
    },

    setAllProperties: (state, action) => {
      state.allProperties = action.payload;
    },
    setLoader: (state, action) => {
      state.abovePropertyLoader = action.payload;
    },
    setAbovePropertiesData: (state, action) => {
      state.aboveProperties = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAbovePropertiesForDialog.fulfilled, (state, action) => {
        state.abovePropertyLoader = false;
        state.abovePropertiesDialog = action?.payload?.properties;
      })

      .addCase(fetchInitiativeFilterAsync.fulfilled, (state, action: any) => {
        state.initiativeFilterValues = Object.values(action.payload)
          ?.filter((value) => value !== 200)
          .map((item: any) => {
            return { name: item };
          });
      })
      .addCase(fetchAssignedLibraryPlansAsync.pending, (state, action: any) => {
        state.abovePropertyLoader = action.meta.arg.pageable.page === 0;
      })
      .addCase(fetchAssignedLibraryPlansAsync.fulfilled, (state, action: any) => {
        state.abovePropertyLoader = false;
        state.assignedLibraryPlan =
          action.meta.arg.pageable.page === 0
            ? action.payload.plans
            : [...state.assignedLibraryPlan, ...action.payload.plans];
        state.totalRecords = action.payload?.meta?.page?.total;
      })
      .addCase(fetchBookmarkedInncodeAsync.fulfilled, (state, action: any) => {
        state.abovePropertyLoader = false;
        state.bookmarkedProperties = action.payload?.bookmarks || [];
      })
      .addCase(fetchBookmarkPlan.pending, (state, action: any) => {
        state.bookmarkLoader = true;
      })
      .addCase(fetchBookmarkPlan.fulfilled, (state, action: any) => {
        state.bookmarkLoader = false;
        state.bookmarkedIdList = action.payload?.bookmarkPlans;
        state.bookmarkedTemplate = action.payload?.bookmarkTemplatePlans;
      })
      .addCase(fetchBookmarkPlan.rejected, (state, action: any) => {
        state.bookmarkLoader = false;
      })
      .addCase(updateBookmarkedPlan.pending, (state, action: any) => {
        state.abovePropertyLoader = true;
      })
      .addCase(updateBookmarkedPlan.fulfilled, (state, action: any) => {
        state.abovePropertyLoader = false;
        if (action?.meta?.arg?.viewType === abovePropertyViewTypePlan)
          state.bookmarkedIdList = action.payload?.bookmarkPlans;
        else if (action?.meta?.arg?.viewType === abovePropertyViewTypeTemplate)
          state.bookmarkedTemplate = action.payload?.bookmarkTemplatePlans;
      })
      .addCase(updateBookmarkedPlan.rejected, (state) => {
        state.abovePropertyLoader = false;
      })
      .addCase(updateBookmarkedInncodesAsync.pending, (state, action: any) => {
        state.abovePropertyLoader = true;
      })
      .addCase(updateBookmarkedInncodesAsync.fulfilled, (state, action: any) => {
        state.abovePropertyLoader = false;
        state.bookmarkedProperties = action.payload?.bookmarks || [];
      })
      .addCase(updateBookmarkedInncodesAsync.rejected, (state, action: any) => {
        state.abovePropertyLoader = false;
      })
      .addCase(fetchAbovePropertyMyPlansAsync.fulfilled, (state, action: any) => {
        state.planProgress.meta = action.payload.meta;
        state.planProgress.plansList =
          action.meta.arg.page === 0
            ? action.payload.userplans
            : [...state.planProgress.plansList, ...action.payload.userplans];
        state.planProgress.totalList = action.payload?.meta?.page?.total;
        state.planProgress.owners = action.payload.meta.owners;
        state.planProgress.inncodes = uniq(action.payload.meta?.filters.map((item: any) => item.innCode));
        state.brands = uniqBy(action.payload.meta?.filters, "brandCode");
      })
      .addCase(fetchPropertyInfoAsync.fulfilled, (state, action: any) => {
        const { status, ...response } = action.payload;
        state.propertyList = Object.values(response);
      })
      .addCase(fetchAssigneeDetailsByHtlIdsAsync.fulfilled, (state, action) => {
        state.roleCategoryList = action.payload.users;
      })
      .addCase(fetchMultiTargetDetails.pending, (state, action: any) => {
        state.abovePropertyLoader = action.meta?.arg?.pagination?.page === 1;
      })
      .addCase(fetchMultiTargetDetails.fulfilled, (state, action: any) => {
        state.abovePropertyLoader = false;
        state.multiTargetDetails =
          action.meta?.arg?.pagination?.page === 1
            ? action.payload.plans
            : [...state.multiTargetDetails, ...action.payload.plans];
        state.multiTargetTotalRecords = action.payload?.meta?.page?.total;
      })
      .addCase(fetchUnImplementedProperties.fulfilled, (state, action: any) => {
        state.planProgress.unImplementedProperties =
          action.meta.arg.page === 0
            ? action.payload.userplans
            : [...state.planProgress.unImplementedProperties, ...action.payload.userplans];
        state.planProgress.unImplemenetedCount = action.payload?.meta?.page?.total;
        state.abovePropertyLoader = false;
      })
      .addCase(deleteMyPlansAsync.fulfilled, (state, action: any) => {
        state.planProgress.plansList = state.planProgress.plansList.filter(
          (plan: any) => plan.id !== action?.meta?.arg?.id
        );
        state.planProgress.totalList = state.planProgress.totalList - 1;
      })
      .addCase(addToMyPlansAsync.fulfilled, (state, action: any) => {
        if (action?.meta?.arg?.shouldDataRefresh) state.planProgress.createNewPlan = action.payload;
      })
      .addCase(updatePlanAsync.fulfilled, (state, action: any) => {
        state.planProgress.plansList = state.planProgress.plansList.map((element: any) => {
          if (element.id === action.payload.id) {
            element = { ...element, ...action.payload, title: action.payload.name };
          }
          return element;
        });
      })
      .addCase(fetchSnapshotData.fulfilled, (state, action: any) => {
        state.planProgress.snapshotData = action.payload.meta;
      })
      .addCase(fetchPlansSummary.pending, (state, action) => {
        state.progressSummaryLoader = true;
      })
      .addCase(fetchPlansSummary.fulfilled, (state, action) => {
        state.planProgressSummary = action.payload.plans?.[0];
        state.progressSummaryLoader = false;
      })
      .addCase(fetchPlansSummary.rejected, (state, action) => {
        state.progressSummaryLoader = false;
      })
      .addCase(fetchAbovePropertyMyPlansAsync.rejected, (state, action) => {
        state.abovePropertyLoader = false;
      })
      .addMatcher(isPending(fetchAbovePropertiesForDialog), (state, action) => {
        state.abovePropertyLoader = true;
      })
      .addMatcher(isPending(fetchAbovePropertyMyPlansAsync, fetchUnImplementedProperties), (state, action) => {
        state.abovePropertyLoader = action.meta.arg.page === 0;
      })
      .addMatcher(isRejectedAction, (state, action) => {
        if (action.type !== "fetchBookmarkPlan/rejected") state.abovePropertyLoader = false;
      });
  }
});

export const {
  reset,
  clearPlanProgress,
  setBrands,
  setAllProperties,
  clearFilter,
  resetAllFilters,
  setSearchText,
  setAbovePropertyFilters,
  setLoader,
  clearChildFilters,
  setAbovePropertiesData
} = AbovePropertySlice.actions;

export default AbovePropertySlice.reducer;
